import React, { ReactNode, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useCreatePathPickRequests from "@/features/pathpick/hooks/mutation/useCreatePathPickRequests";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import PathPickOrderDetailCard from "@/features/pathpick/features/order-history/features/detail/components/PathPickOrderDetailCard";
import PathPickOrderKreamFileRequestModal from "@/features/pathpick/features/order-history/features/detail/components/document-section/PathPickOrderKreamFileRequestModal";
import PathPickRequestProductsModal from "@/features/pathpick/components/request/PathPickRequestProductsModal";
import { ORDER_DETAIL_KEY } from "@/features/pathpick/features/order-history/features/detail/constants";
import { PathPickOrderDetailResponse } from "@/features/pathpick/features/order-history/types/response";
import { isPathPickOrderQuantityConfirmed } from "@/features/pathpick/features/order-history/utils";
import { selectPathPickOrderKreamFileView } from "@/features/pathpick/features/order-history/features/detail/utils";

interface Props {
  order: PathPickOrderDetailResponse;
  // 시작 가이드처럼 화면을 벗어나면 안 되는 곳에서 `신청 상품 내역 보기` 링크를 감춘다.
  showHistoryAction?: boolean;
  // 시작 가이드가 주문 상세 화면 전체를 미리보기로 띄울 때, 이 카드만 파란 선으로 강조하고
  // 화면 전체에 걸린 클릭 차단(pointer-events)에서 예외로 되살린다.
  isHighlighted?: boolean;
  // 미리보기(시작 가이드)는 데모 주문을 그리므로 실제 신청을 보내지 않는다.
  isPreview?: boolean;
  // 신청을 확인한 뒤 화면별로 덧붙일 후처리 (시작 가이드의 안내 토스트).
  onRequested?: () => void;
}

// KREAM 입찰용 업로드 파일 요청 카드.
// 파일은 확정 수량 기준으로만 만들 수 있어서 수량 확정 전에는 안내와 비활성 버튼만 남긴다
// (카드를 숨기지 않는 것은, 확정 후 뭘 할 수 있는지 미리 알려야 하기 때문).
function PathPickOrderDetailKreamFile({
  order,
  showHistoryAction = true,
  isHighlighted = false,
  isPreview = false,
  onRequested,
}: Props) {
  const { t } = useI18nHelper();
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  // 신청 상품 내역 모달 대상 요청 id (null이면 닫힘)
  const [productsRequestId, setProductsRequestId] = useState<number | null>(
    null
  );
  // 실패 토스트와 신청 내역·사용량 재조회는 이 훅이 이미 담당한다 — 성공 안내만 여기서 붙인다.
  const { mutate: createRequests, isPending: isRequesting } =
    useCreatePathPickRequests();
  const { info } = useAppToasts({ id: "GLOBAL" });

  // 이 카드의 신청은 상품이 아니라 주문의 확정 수량을 기준으로 접수한다.
  const requestKreamFiles = () => {
    if (isPreview) {
      setIsRequestModalOpen(false);
      onRequested?.();
      return;
    }

    createRequests(
      {
        requestTypes: ["KREAM_EXCEL_CONFIRMED_QUANTITY"],
        orderIds: [order.id],
      },
      {
        onSuccess: () => {
          setIsRequestModalOpen(false);
          info(t(`${ORDER_DETAIL_KEY}kream_request_success`));
          onRequested?.();
        },
        // 실패해도 모달은 열어 둔다 — 훅이 띄운 에러 토스트를 보고 바로 다시 시도할 수 있다.
      }
    );
  };

  const isQuantityConfirmed = isPathPickOrderQuantityConfirmed(order);
  // 체험·멤버십 만료 시 파일 생성 신청만 잠근다(주문 상세의 나머지 정산·환불 입력은 그대로).
  // 시작 가이드의 데모 주문(isPreview)은 실제 신청이 아니므로 잠그지 않는다.
  const { isMembershipExpired } = useMembership();
  const isRequestLocked = !isPreview && isMembershipExpired;
  const view = selectPathPickOrderKreamFileView(order);
  // 확정 수량 파일은 주문당 한 번만 만든다 — 이미 신청한 주문은 CTA를 닫고 신청 내역만 보여준다.
  const canRequest =
    isQuantityConfirmed && !isRequestLocked && !view.hasRequested;

  // 확정 전에는 대상/제외가 아직 정해지지 않아 숫자 대신 "확인 중"으로 둔다.
  const counts = [
    t(`${ORDER_DETAIL_KEY}kream_count_requested`, {
      count: view.requestedQuantity,
    }),
    isQuantityConfirmed
      ? t(`${ORDER_DETAIL_KEY}kream_count_target`, {
          count: view.targetQuantity,
        })
      : t(`${ORDER_DETAIL_KEY}kream_count_target_pending`),
    isQuantityConfirmed
      ? t(`${ORDER_DETAIL_KEY}kream_count_excluded`, {
          count: view.excludedQuantity,
        })
      : t(`${ORDER_DETAIL_KEY}kream_count_excluded_pending`),
  ].join(" / ");

  return (
    <KreamFileHighlight isHighlighted={isHighlighted}>
      <PathPickOrderDetailCard
        title={t(`${ORDER_DETAIL_KEY}kream_title`)}
        gap={"12px"}
      >
        <Flex flexDirection={"column"} gap={"8px"}>
          {/* 신청까지 끝난 주문에 "파일 준비 가능"이 남으면 비활성 CTA와 어긋나므로 상태를 셋으로 가른다. */}
          <PathPickBadge
            size={"medium"}
            colorVariant={
              view.hasRequested
                ? "green"
                : isQuantityConfirmed
                ? "blue"
                : "gray"
            }
            label={t(
              view.hasRequested
                ? `${ORDER_DETAIL_KEY}kream_badge_requested`
                : isQuantityConfirmed
                ? `${ORDER_DETAIL_KEY}kream_badge_ready`
                : `${ORDER_DETAIL_KEY}kream_badge_before_confirm`
            )}
          />
          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {counts}
          </Text>
          <Text textStyle={"body-sm-medium"} color={"#444440"}>
            {t(`${ORDER_DETAIL_KEY}kream_description`)}
          </Text>
        </Flex>

        <PathPickInlineNotice tone={"yellow"} leadingIcon={"warning-triangle"}>
          {t(`${ORDER_DETAIL_KEY}kream_warning`)}
        </PathPickInlineNotice>

        <Flex flexDirection={"column"} gap={"8px"}>
          {/* 비활성일 때는 primary의 진회색 대신 white 비활성(연회색)이 시안과 맞다 */}
          <AppButton
            width={"100%"}
            height={"40px"}
            variant={canRequest ? "primary" : "white"}
            isDisabled={!canRequest}
            // 이미 신청한 상태가 멤버십 잠금보다 되돌릴 수 없는 사유라 먼저 안내한다.
            disabledMessage={
              view.hasRequested
                ? t(`${ORDER_DETAIL_KEY}kream_already_requested_tooltip`)
                : isRequestLocked
                ? t("path_pick:membership_lock.disabled_tooltip")
                : undefined
            }
            onClick={() => setIsRequestModalOpen(true)}
          >
            <Flex gap={"6px"} alignItems={"center"} justifyContent={"center"}>
              <CustomIcon
                src={"document-text"}
                width={16}
                height={16}
                color={canRequest ? "#FFFFFF" : "#8F8F8C"}
              />
              <Text textStyle={"body-sm-bold"}>
                {t(`${ORDER_DETAIL_KEY}kream_request_button`)}
              </Text>
            </Flex>
          </AppButton>
          {/* 버튼이 아니라 텍스트 링크다 — 밑줄이 글자 폭만큼만 그어지도록 폭을 글자에 맞춘다.
              신청 전에는 열어 볼 요청이 없어 링크 자체를 두지 않는다. */}
          {showHistoryAction && view.requestId !== null && (
            <Text
              textStyle={"body-sm-bold"}
              color={"#1272EF"}
              alignSelf={"center"}
              width={"fit-content"}
              cursor={"pointer"}
              _hover={{ textDecoration: "underline" }}
              onClick={() => setProductsRequestId(view.requestId)}
            >
              {t(`${ORDER_DETAIL_KEY}kream_view_request_products`)}
            </Text>
          )}
          <Text
            textStyle={"caption-medium"}
            color={"#8F8F8C"}
            textAlign={"center"}
          >
            {t(`${ORDER_DETAIL_KEY}kream_footnote`)}
          </Text>
        </Flex>

        {/* 모달은 닫을 때 언마운트되어 상태가 초기화된다 */}
        {isRequestModalOpen && (
          <PathPickOrderKreamFileRequestModal
            isOpen={isRequestModalOpen}
            onClose={() => setIsRequestModalOpen(false)}
            orderNumber={order.orderNumber}
            confirmedKindCount={view.confirmedKindCount}
            confirmedQuantity={view.targetQuantity}
            onConfirm={requestKreamFiles}
            isRequesting={isRequesting}
          />
        )}

        {/* 신청 상품 내역 — 등록 신청 내역 페이지로 나가지 않고 이 화면에서 그대로 확인한다.
            요청 헤더(구분·신청일시·건수)까지 /pathpick/requests/{id} 응답에서 읽어 id만 넘긴다. */}
        {productsRequestId !== null && (
          <PathPickRequestProductsModal
            isOpen
            onClose={() => setProductsRequestId(null)}
            requestId={productsRequestId}
          />
        )}
      </PathPickOrderDetailCard>
    </KreamFileHighlight>
  );
}

// 강조가 꺼져 있으면 래퍼 요소 자체를 만들지 않는다 — 실제 화면의 DOM을 늘리지 않기 위해서다.
function KreamFileHighlight({
  isHighlighted,
  children,
}: {
  isHighlighted: boolean;
  children: ReactNode;
}) {
  const { t } = useI18nHelper();

  if (!isHighlighted) {
    return <>{children}</>;
  }

  return (
    <Box
      role={"group"}
      aria-label={t("path_pick_guide:order_detail.highlight_aria_label")}
      outline={"2px solid #1272EF"}
      outlineOffset={"3px"}
      borderRadius={"8px"}
      // 미리보기는 화면 전체의 클릭을 막아 두므로, 이 카드만 다시 켠다.
      pointerEvents={"auto"}
    >
      {children}
    </Box>
  );
}

export default PathPickOrderDetailKreamFile;
